import type {
    AddCommerceCartLineRequest,
    CommerceCatalogueExperience,
    CommerceCartPurchaseType,
    UpdateCommerceCartLineQuantityRequest,
} from "@teqtank/commerce-contracts";

export function normalizeAddCartLineRequest(
    request: AddCommerceCartLineRequest,
    expectedVersion: number | undefined,
): AddCommerceCartLineRequest {
    if (request === null || request === undefined || typeof request !== "object")
        throw new TypeError("Cart add request is required.");
    const productId = requireIdentifier(request.productId, "Product ID");
    const variantId =
        request.variantId === undefined
            ? undefined
            : requireIdentifier(request.variantId, "Variant ID");
    const purchaseType: CommerceCartPurchaseType = request.purchaseType ?? "one-time";
    if (purchaseType !== "one-time" && purchaseType !== "recurring")
        throw new TypeError("Cart purchaseType must be one-time or recurring.");
    const version = request.expectedVersion ?? expectedVersion;
    const experience = normalizeExperience(request.experience);
    return Object.freeze({
        productId,
        quantity: requireQuantity(request.quantity),
        purchaseType,
        ...(purchaseType === "recurring" && request.frequencyId !== undefined
            ? { frequencyId: requireIdentifier(request.frequencyId, "Recurring frequency ID") }
            : {}),
        ...(purchaseType === "recurring" && request.frequencyDays !== undefined
            ? { frequencyDays: request.frequencyDays }
            : {}),
        ...(variantId !== undefined ? { variantId } : {}),
        ...(version !== undefined ? { expectedVersion: version } : {}),
        ...(experience !== undefined ? { experience } : {}),
        ...(request.replaceCart === true ? { replaceCart: true } : {}),
    });
}

function normalizeExperience(
    value: CommerceCatalogueExperience | undefined,
): CommerceCatalogueExperience | undefined {
    return value === undefined
        ? undefined
        : (requireIdentifier(value, "Catalogue experience") as CommerceCatalogueExperience);
}

export function normalizeUpdateQuantityRequest(
    request: UpdateCommerceCartLineQuantityRequest,
    expectedVersion: number | undefined,
): UpdateCommerceCartLineQuantityRequest {
    if (request === null || request === undefined || typeof request !== "object")
        throw new TypeError("Cart quantity request is required.");
    const version = request.expectedVersion ?? expectedVersion;
    return Object.freeze({
        lineId: requireIdentifier(request.lineId, "Cart line ID"),
        quantity: requireQuantity(request.quantity),
        ...(version !== undefined ? { expectedVersion: version } : {}),
    });
}

export function requireIdentifier(value: string, description: string): string {
    if (typeof value !== "string" || value.trim().length === 0)
        throw new TypeError(`${description} must contain at least one non-whitespace character.`);
    return value.trim();
}
export function requireQuantity(value: number): number {
    if (!Number.isInteger(value) || value < 1 || value > 999)
        throw new TypeError("Cart quantity must be an integer between one and 999.");
    return value;
}
export function requireCouponCode(value: string): string {
    return requireIdentifier(value, "Coupon code").toUpperCase();
}
